import React, { useCallback, useEffect, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { useParams } from 'react-router';

import {
    useActivateCustomTunnelMutation,
    useDeletePendingTunnelMutation,
    useGetSubscriptionsQuery,
    useGetTunnelsQuery,
} from 'api/main';
import { useDialog } from 'utils/hooks';
import { questionType } from 'utils/constants';
import { getEntityIdFromIRI } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { ActionButton, ConfirmDialog, Loader } from 'components';
import { Box } from '@mui/material';

const TunnelMenu = ({ onManage, onShowActiveVersion }) => {
    const { fundId } = useParams();
    const {
        open: confirmDeleteOpen,
        onClose: handleCloseDelete,
        onOpen: handleDeleteOpen,
    } = useDialog();

    const { data: fundTunnels = [], isLoading: tunnelLoader } = useGetTunnelsQuery({
        investmentFunds: fundId,
    });
    const { data: { subscriptions = [] } = { subscriptions: [] }, isLoading: subscriptionLoader } =
        useGetSubscriptionsQuery({ product: [+fundId], page: 1 });

    const pendingTunnels = useMemo(
        () => fundTunnels.filter(tunnel => tunnel.status === 'pending'),
        [fundTunnels]
    );
    const activeTunnels = useMemo(
        () => fundTunnels.filter(tunnel => tunnel.status === 'active'),
        [fundTunnels]
    );
    const pendingTunnelCanBeActivated = useMemo(() => {
        let customSteps = pendingTunnels?.[0]?.tunnelPhases?.[0]?.tunnelSteps?.filter(
            ts => ts.step.name === texts.MANUALLY_CREATED_STEP
        );
        let hasIncompleteSteps = customSteps?.find(
            ts =>
                !ts.questions.length ||
                ts.questions.find(q =>
                    q.type === questionType.TEXT ? q.answers.length < 1 : q.answers.length < 2
                )
        );
        return !hasIncompleteSteps;
    }, [pendingTunnels]);
    const activeVersionSubscriptions = useMemo(
        () =>
            subscriptions.filter(s =>
                activeTunnels.find(tunnel => tunnel.id === getEntityIdFromIRI(s.tunnel, 'tunnels'))
            ),
        [activeTunnels, subscriptions]
    );

    const [deleteTunnel, { isLoading: deleteLoader, isSuccess: deleteSuccess }] =
        useDeletePendingTunnelMutation();
    const [activateTunnel, { isLoading: activateLoader }] = useActivateCustomTunnelMutation();

    const handleDeleteTunnel = useCallback(() => {
        deleteTunnel(fundId);
    }, [deleteTunnel, fundId]);

    const handleActivateTunnel = useCallback(() => {
        activateTunnel(fundId);
    }, [activateTunnel, fundId]);

    useEffect(() => {
        if (deleteSuccess) {
            handleCloseDelete();
        }
    }, [deleteSuccess, handleCloseDelete]);

    return (
        <Loader loading={tunnelLoader || subscriptionLoader}>
            <Box
                sx={{
                    padding: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                <ConfirmDialog
                    open={confirmDeleteOpen}
                    handleConfirm={handleDeleteTunnel}
                    handleClose={handleCloseDelete}
                    title={texts.DELETE_TUNNEL_VERSION}
                    loading={deleteLoader}
                    confirmText={texts.CONFIRMER}
                    cancelText={texts.CANCEL}
                >
                    {texts.CONFIRM_DELETE_TUNNEL_VERSION}
                </ConfirmDialog>
                <ActionButton variant="contained" onClick={onManage}>
                    {texts.MANAGE_TUNNEL}
                </ActionButton>
                <ActionButton variant="contained" onClick={onShowActiveVersion}>
                    {texts.CONSULT_ACTIVE_TUNNEL_VERSION}
                </ActionButton>
                <ActionButton
                    variant="contained"
                    disabled={
                        (!!activeVersionSubscriptions.length &&
                            !!activeTunnels.length &&
                            !pendingTunnels.length) ||
                        (!activeTunnels.length && !pendingTunnels.length)
                    }
                    onClick={handleDeleteOpen}
                >
                    {texts.SUPPRIMER}
                </ActionButton>
                <ActionButton
                    variant="contained"
                    disabled={!pendingTunnels.length || !pendingTunnelCanBeActivated}
                    loading={activateLoader}
                    onClick={handleActivateTunnel}
                >
                    {texts.ACTIVATE_TUNNEL_VERSION}
                </ActionButton>
            </Box>
        </Loader>
    );
};

TunnelMenu.propTypes = {
    onManage: PropTypes.func.isRequired,
    onShowActiveVersion: PropTypes.func.isRequired,
};

export default TunnelMenu;
